/**
 * File System API storage for save games
 * Uses Origin Private File System if available, falls back to localStorage
 */

interface SaveMetadata {
    id: number;
    name: string;
    date: string;
}

interface SaveGameData {
    id: number;
    name: string;
    date: string;
    gameData: any;
    cheatFreezes?: any;
    zMainText?: string;
    currentImage?: string;
}

let fileSystemRoot: FileSystemDirectoryHandle | null = null;
let fileSystemAvailable: boolean | null = null;

/**
 * Check if File System API is available
 */
export async function checkFileSystemAvailability(): Promise<boolean> {
    if (fileSystemAvailable !== null) {
        return fileSystemAvailable;
    }

    // Check for Origin Private File System support
    if (!('navigator' in window) || !('storage' in navigator) || !('getDirectory' in (navigator.storage as any))) {
        fileSystemAvailable = false;
        return false;
    }

    try {
        // Try to get Origin Private File System directory
        fileSystemRoot = await (navigator.storage as any).getDirectory();
        fileSystemAvailable = true;
        return true;
    } catch (e) {
        fileSystemAvailable = false;
        return false;
    }
}

/**
 * Get saves directory for a specific game
 */
async function getSavesDirectory(gameTitle: string): Promise<FileSystemDirectoryHandle | null> {
    if (!fileSystemRoot) {
        const available = await checkFileSystemAvailability();
        if (!available) {
            return null;
        }
    }

    if (!fileSystemRoot) {
        return null;
    }

    try {
        // Sanitize game title for directory name
        const sanitized = gameTitle.replace(/[^a-zA-Z0-9_-]/g, '_');
        const dirName = `saves_${sanitized}`;

        // Try to get existing directory
        try {
            return await fileSystemRoot.getDirectoryHandle(dirName);
        } catch {
            // Directory doesn't exist, create it
            return await fileSystemRoot.getDirectoryHandle(dirName, { create: true });
        }
    } catch (e) {
        console.error('Failed to get saves directory:', e);
        return null;
    }
}

/**
 * File System storage operations with localStorage fallback
 */
export const FileSystemSaveStorage = {
    /**
     * Check if File System API is available
     */
    async isAvailable(): Promise<boolean> {
        return await checkFileSystemAvailability();
    },

    /**
     * Get save index (metadata for all saves)
     */
    async getIndex(gameTitle: string): Promise<{ [key: string]: SaveMetadata } | null> {
        const available = await checkFileSystemAvailability();
        if (!available) {
            // Fallback to localStorage
            const key = gameTitle + '-Saves';
            const rawIndex = localStorage.getItem(key);
            return rawIndex ? JSON.parse(rawIndex) : null;
        }

        try {
            const savesDir = await getSavesDirectory(gameTitle);
            if (!savesDir) {
                return null;
            }

            const index: { [key: string]: SaveMetadata } = {};

            // Iterate through all save directories
            for await (const [name, handle] of (savesDir as any).entries()) {
                if (handle.kind === 'directory') {
                    try {
                        const saveId = parseInt(name.replace('save_', ''), 10);
                        if (isNaN(saveId)) continue;

                        // Read metadata.json
                        const metadataFile = await handle.getFileHandle('metadata.json');
                        const metadataBlob = await metadataFile.getFile();
                        const metadataText = await metadataBlob.text();
                        const metadata: SaveMetadata = JSON.parse(metadataText);

                        index[saveId] = metadata;
                    } catch (e) {
                        // Skip invalid save directories
                        console.warn(`Failed to read metadata for ${name}:`, e);
                    }
                }
            }

            return Object.keys(index).length > 0 ? index : null;
        } catch (e) {
            console.error('Failed to get save index from File System:', e);
            // Fallback to localStorage
            const key = gameTitle + '-Saves';
            const rawIndex = localStorage.getItem(key);
            return rawIndex ? JSON.parse(rawIndex) : null;
        }
    },

    /**
     * Store save index (metadata for all saves)
     */
    async setIndex(gameTitle: string, index: { [key: string]: SaveMetadata }): Promise<void> {
        const available = await checkFileSystemAvailability();
        if (!available) {
            // Fallback to localStorage
            const key = gameTitle + '-Saves';
            localStorage.setItem(key, JSON.stringify(index));
            return;
        }

        // Index is stored implicitly in the directory structure
        // We don't need a separate index file, but we'll keep localStorage as backup
        const key = gameTitle + '-Saves';
        localStorage.setItem(key, JSON.stringify(index));
    },

    /**
     * Store a save game
     */
    async setSave(gameTitle: string, id: number, saveData: SaveGameData): Promise<void> {
        const available = await checkFileSystemAvailability();
        if (!available) {
            // Fallback to localStorage
            const key = gameTitle + '-Save' + id;
            localStorage.setItem(key, JSON.stringify(saveData));
            return;
        }

        try {
            const savesDir = await getSavesDirectory(gameTitle);
            if (!savesDir) {
                throw new Error('Failed to get saves directory');
            }

            const saveDirName = `save_${id}`;
            let saveDir: FileSystemDirectoryHandle;
            try {
                saveDir = await savesDir.getDirectoryHandle(saveDirName);
            } catch {
                saveDir = await savesDir.getDirectoryHandle(saveDirName, { create: true });
            }

            // Store metadata
            const metadata: SaveMetadata = {
                id: saveData.id,
                name: saveData.name,
                date: saveData.date
            };
            const metadataFile = await saveDir.getFileHandle('metadata.json', { create: true });
            const metadataWritable = await metadataFile.createWritable();
            await metadataWritable.write(JSON.stringify(metadata, null, 2));
            await metadataWritable.close();

            // Store game data
            const gameDataFile = await saveDir.getFileHandle('gameData.json', { create: true });
            const gameDataWritable = await gameDataFile.createWritable();
            await gameDataWritable.write(JSON.stringify(saveData, null, 2));
            await gameDataWritable.close();
        } catch (e) {
            console.error('Failed to store save in File System:', e);
            // Fallback to localStorage
            const key = gameTitle + '-Save' + id;
            localStorage.setItem(key, JSON.stringify(saveData));
        }
    },

    /**
     * Get a save game
     */
    async getSave(gameTitle: string, id: number): Promise<SaveGameData | null> {
        const available = await checkFileSystemAvailability();
        if (!available) {
            // Fallback to localStorage
            const key = gameTitle + '-Save' + id;
            const saveStr = localStorage.getItem(key);
            return saveStr ? JSON.parse(saveStr) : null;
        }

        try {
            const savesDir = await getSavesDirectory(gameTitle);
            if (!savesDir) {
                return null;
            }

            const saveDirName = `save_${id}`;
            const saveDir = await savesDir.getDirectoryHandle(saveDirName);

            // Read gameData.json
            const gameDataFile = await saveDir.getFileHandle('gameData.json');
            const gameDataBlob = await gameDataFile.getFile();
            const gameDataText = await gameDataBlob.text();
            const gameData: SaveGameData = JSON.parse(gameDataText);

            return gameData;
        } catch (e) {
            console.error('Failed to get save from File System:', e);
            // Fallback to localStorage
            const key = gameTitle + '-Save' + id;
            const saveStr = localStorage.getItem(key);
            return saveStr ? JSON.parse(saveStr) : null;
        }
    },

    /**
     * Delete a save game
     */
    async deleteSave(gameTitle: string, id: number): Promise<void> {
        const available = await checkFileSystemAvailability();
        if (!available) {
            // Fallback to localStorage
            const key = gameTitle + '-Save' + id;
            localStorage.removeItem(key);
            return;
        }

        try {
            const savesDir = await getSavesDirectory(gameTitle);
            if (!savesDir) {
                return;
            }

            const saveDirName = `save_${id}`;
            await savesDir.removeEntry(saveDirName, { recursive: true });
        } catch (e) {
            console.error('Failed to delete save from File System:', e);
            // Fallback to localStorage
            const key = gameTitle + '-Save' + id;
            localStorage.removeItem(key);
        }
    },

    /**
     * Clear all saves for a game
     */
    async clear(gameTitle: string): Promise<void> {
        const available = await checkFileSystemAvailability();
        if (!available) {
            // Fallback to localStorage
            const indexKey = gameTitle + '-Saves';
            const rawIndex = localStorage.getItem(indexKey);
            if (rawIndex) {
                const index = JSON.parse(rawIndex);
                const saveKeys = Object.keys(index);
                for (const saveId of saveKeys) {
                    localStorage.removeItem(gameTitle + '-Save' + saveId);
                }
            }
            localStorage.setItem(indexKey, JSON.stringify({}));
            return;
        }

        try {
            const savesDir = await getSavesDirectory(gameTitle);
            if (!savesDir) {
                return;
            }

            // Delete all save directories
            for await (const [name, handle] of (savesDir as any).entries()) {
                if (handle.kind === 'directory' && name.startsWith('save_')) {
                    await savesDir.removeEntry(name, { recursive: true });
                }
            }

            // Clear localStorage index
            const indexKey = gameTitle + '-Saves';
            localStorage.setItem(indexKey, JSON.stringify({}));
        } catch (e) {
            console.error('Failed to clear saves from File System:', e);
            // Fallback to localStorage
            const indexKey = gameTitle + '-Saves';
            const rawIndex = localStorage.getItem(indexKey);
            if (rawIndex) {
                const index = JSON.parse(rawIndex);
                const saveKeys = Object.keys(index);
                for (const saveId of saveKeys) {
                    localStorage.removeItem(gameTitle + '-Save' + saveId);
                }
            }
            localStorage.setItem(indexKey, JSON.stringify({}));
        }
    },

    /**
     * Migrate saves from localStorage to File System API
     */
    async migrateFromLocalStorage(gameTitle: string): Promise<void> {
        const available = await checkFileSystemAvailability();
        if (!available) {
            return;
        }

        const indexKey = gameTitle + '-Saves';
        const rawIndex = localStorage.getItem(indexKey);

        if (!rawIndex) {
            return;
        }

        try {
            const index = JSON.parse(rawIndex);
            const saveKeys = Object.keys(index);

            // Check if already migrated (has saves in File System)
            const existingIndex = await this.getIndex(gameTitle);
            if (existingIndex && Object.keys(existingIndex).length > 0) {
                return; // Already migrated
            }

            // Migrate each save
            for (const saveId of saveKeys) {
                const saveKey = gameTitle + '-Save' + saveId;
                const saveStr = localStorage.getItem(saveKey);
                if (saveStr) {
                    try {
                        const saveData = JSON.parse(saveStr);
                        await this.setSave(gameTitle, parseInt(saveId, 10), saveData);
                    } catch (e) {
                        console.error(`Failed to migrate save ${saveId}:`, e);
                        // Continue with other saves
                    }
                }
            }

            // Update index
            await this.setIndex(gameTitle, index);
        } catch (e) {
            console.error('Migration from localStorage failed:', e);
        }
    }
};
